// zbot-Live-Freischaltung — Overlay des Profil-Panels.
//
// Zeigt den vom Server per CDP-Screencast uebertragenen Chrome-Tab als Canvas und
// leitet Maus-/Tastatureingaben zurueck, damit eine Sicherheitsabfrage (DDoS-Guard/
// hCaptcha) direkt hier geloest werden kann, ohne lokales X11-Forwarding.
//
// Wird von profile-panel.js lazy erzeugt, sobald ein Admin "Freischalten" zum ersten
// Mal anklickt. Nur fuer Rolle `admin` erreichbar (der Button rendert sonst gar nicht);
// die Route prueft zusaetzlich serverseitig (s. zbot_unlock_admin.rs).
//
// Nur auf papers.mednos.eu funktional - ruft relative Pfade auf der aktuellen Origin
// auf, weil zbot ausschliesslich an Papers haengt (kein Cross-Origin-Aufruf zu einer
// anderen App noetig oder vorgesehen).

import { ppButton } from './platform-api.js';

const CSS = `
.mednos-freischalt-backdrop {
  position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5);
  z-index: 10010; display: none;
}
.mednos-freischalt-backdrop.is-open { display: block; }
.mednos-freischalt {
  position: fixed; left: 50%; top: 50%; transform: translate(-50%, -50%);
  padding: 16px; display: none; flex-direction: column; gap: 10px;
  max-width: 94vw; max-height: 94vh;
  background: #1a1a1a; color: #fff;
  border: none; border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  z-index: 10011;
  font-family: 'Inter', system-ui, sans-serif;
}
.mednos-freischalt.is-open { display: flex; }
.mednos-freischalt__kopf { display: flex; align-items: center; gap: 12px; }
.mednos-freischalt__titel { font-size: 1rem; font-weight: 600; }
.mednos-freischalt__status { font-size: 0.78rem; color: #b0b0b0; margin-left: auto; }
/* Das Bild darf schrumpfen, aber nie wachsen: Herunterskalieren bleibt scharf,
   Hochskalieren zerfliesst. max-width/max-height mit height:auto haelt dabei das
   Seitenverhaeltnis - eine Verzerrung wuerde die zurueckgerechneten Klickkoordinaten
   verschieben (s. berechneSeitenKoordinate). */
.mednos-freischalt canvas {
  display: block; border-radius: 6px; cursor: default; background: #000;
  max-width: 100%; max-height: calc(94vh - 7rem); height: auto;
}
.mednos-freischalt__leiste { display: flex; gap: 8px; }
`;

let styleEingefuegt = false;
function stelleStylesSicher() {
  if (styleEingefuegt) return;
  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.append(style);
  styleEingefuegt = true;
}

/**
 * Rechnet einen Klick im Canvas in Seitenkoordinaten des gecasteten Tabs um.
 *
 * Pur und exportiert, damit genau diese Umrechnung testbar ist: sie war schon einmal
 * falsch, weil der Canvas fest auf 1280x800 stand, das Screencast-Bild aber die echte
 * (meist kleinere) Viewport-Groesse hat - jeder Klick landete dadurch versetzt. Seit
 * das Canvas bei jedem Frame auf die Bildgroesse gesetzt wird, ist die Umrechnung nur
 * noch die CSS-Skalierung (Canvas-Pixel je Layout-Pixel).
 *
 * @param {number} clientX Fensterkoordinate des Zeigers
 * @param {number} clientY Fensterkoordinate des Zeigers
 * @param {{left: number, top: number, width: number, height: number}} rechteck
 *   Layout-Rechteck des Canvas (getBoundingClientRect)
 * @param {number} canvasBreite canvas.width (Bildpunkte, nicht CSS-Pixel)
 * @param {number} canvasHoehe canvas.height
 * @returns {{x: number, y: number}}
 */
export function berechneSeitenKoordinate(clientX, clientY, rechteck, canvasBreite, canvasHoehe) {
  return {
    x: (clientX - rechteck.left) * (canvasBreite / rechteck.width),
    y: (clientY - rechteck.top) * (canvasHoehe / rechteck.height),
  };
}

export function initZbotUnlockOverlay() {
  stelleStylesSicher();

  const backdrop = document.createElement('div');
  backdrop.className = 'mednos-freischalt-backdrop';
  const box = document.createElement('div');
  box.className = 'mednos-freischalt';

  const kopf = document.createElement('div');
  kopf.className = 'mednos-freischalt__kopf';
  kopf.innerHTML = `<span class="mednos-freischalt__titel">zbot-Freischaltung</span>
    <span class="mednos-freischalt__status"></span>`;
  const statusEl = kopf.querySelector('.mednos-freischalt__status');

  const canvas = document.createElement('canvas');
  // Nur die Startgroesse, bis das erste Frame da ist: ab dann uebernimmt der Canvas
  // die echte Bildgroesse (s. Frame-Handler unten), sonst zeigen Klicks daneben.
  canvas.width = 1280;
  canvas.height = 800;
  canvas.tabIndex = 0; // fokussierbar, sonst kommen keine Tastatur-Events an

  const leiste = document.createElement('div');
  leiste.className = 'mednos-freischalt__leiste';
  // ppButton statt nackter <button>: gibt beiden die Klasse `profile-panel__btn` und
  // damit denselben Look wie jeder andere Knopf der Plattform. Die Regeln dazu stehen
  // im <style> von profile-panel.js, das hier garantiert schon haengt - dieses Overlay
  // wird ausschliesslich aus initProfilePanel heraus erzeugt (wie admin-overlay.js).
  const naechsteKnopf = ppButton('Nächste Quelle', () => weiterZurNaechstenQuelle());
  const fertigKnopf = ppButton('Fertig', () => schliessen());
  leiste.append(naechsteKnopf, fertigKnopf);

  box.append(kopf, canvas, leiste);
  document.body.append(backdrop, box);

  let ws = null;
  let quellen = [];
  // Kennung der laufenden Session, aus der Antwort von /start. Der Stop-Aufruf muss
  // sagen, WELCHE Session er meint - sonst wuerde ein vergessener Tab hier eine
  // laengst neu gestartete Session eines anderen Admins beenden.
  let sessionId = null;
  let aktuelleQuelle = 0;

  function zeigeQuellenstand() {
    statusEl.textContent = quellen.length ? `${aktuelleQuelle + 1} von ${quellen.length}` : '';
  }

  /** Wird nach dem Schliessen aufgerufen — die Konsole holt sich damit den
   *  Nutzer zurueck, statt ihn auf der nackten Seite stehen zu lassen. */
  let beiFertig = null;

  function schliessen() {
    if (ws) { ws.close(); ws = null; }
    backdrop.classList.remove('is-open');
    box.classList.remove('is-open');
    if (sessionId === null) return; // nie gestartet oder schon gestoppt
    fetch(`/api/admin/zbot-unlock/stop?session_id=${encodeURIComponent(sessionId)}`, {
      method: 'POST',
      credentials: 'include',
    });
    sessionId = null;
    const zurueck = beiFertig;
    beiFertig = null;
    if (zurueck) zurueck();
  }

  backdrop.addEventListener('click', schliessen);

  function weiterZurNaechstenQuelle() {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ typ: 'naechste_quelle' }));
    // Der Server zaehlt zyklisch weiter (unlock_live::naechster_quellen_index) und
    // schickt keine Bestaetigung - die Anzeige zaehlt deshalb hier mit, mit demselben
    // Umbruch. Ohne das blieb der Text stur bei "1 von N".
    if (!quellen.length) return;
    aktuelleQuelle = (aktuelleQuelle + 1) % quellen.length;
    zeigeQuellenstand();
  }

  const ctx = canvas.getContext('2d');

  function sendeMaus(aktion, ev) {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const { x, y } = berechneSeitenKoordinate(
      ev.clientX,
      ev.clientY,
      canvas.getBoundingClientRect(),
      canvas.width,
      canvas.height,
    );
    ws.send(JSON.stringify({ typ: 'maus', aktion, x, y, taste: 'left' }));
  }
  canvas.addEventListener('mousedown', (ev) => { ev.preventDefault(); sendeMaus('press', ev); });
  canvas.addEventListener('mouseup', (ev) => { ev.preventDefault(); sendeMaus('release', ev); });

  // Bewegungen gedrosselt: ungedrosselt schickt der Browser bis zu mehreren
  // hundert Ereignissen je Sekunde, jedes einzeln durch WebSocket, Papers-Relay
  // und CDP. Das hat die Fernsteuerung zaeh gemacht, ohne dass es etwas bringt —
  // fuer einen Zeiger reichen 25 Aktualisierungen je Sekunde. Druecken und
  // Loslassen laufen bewusst UNGEDROSSELT: ein verschluckter Klick waere fatal.
  const BEWEGUNG_MS = 40;
  let letzteBewegung = 0;
  canvas.addEventListener('mousemove', (ev) => {
    const jetzt = performance.now();
    if (jetzt - letzteBewegung < BEWEGUNG_MS) return;
    letzteBewegung = jetzt;
    sendeMaus('move', ev);
  });
  canvas.addEventListener('keydown', (ev) => {
    ev.preventDefault();
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const text = ev.key.length === 1 ? ev.key : undefined;
    ws.send(JSON.stringify({ typ: 'taste', aktion: 'down', code: ev.code, text }));
  });
  canvas.addEventListener('keyup', (ev) => {
    ev.preventDefault();
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ typ: 'taste', aktion: 'up', code: ev.code }));
  });

  return {
    /**
     * @param {string} [quelle] Genau diese Quelle freischalten. Ohne Angabe
     *   oeffnet der Server alle, die gerade nicht liefern.
     * @param {() => void} [zurueck] Wird nach "Fertig" aufgerufen.
     */
    async oeffnen(quelle, zurueck) {
      return this.oeffnenMitPfad(
        quelle
          ? `/api/admin/zbot-unlock/start?quelle=${encodeURIComponent(quelle)}`
          : '/api/admin/zbot-unlock/start',
        zurueck,
      );
    },

    /**
     * Wie `oeffnen`, nimmt aber den fertigen Startpfad entgegen — der
     * institutionelle Zugang braucht `?ziel=shib` statt einer Quelle.
     *
     * @param {string} pfad Startpfad auf der Papers-Origin
     * @param {() => void} [zurueck] Wird nach "Fertig" aufgerufen.
     */
    async oeffnenMitPfad(pfad, zurueck) {
      beiFertig = zurueck || null;
      backdrop.classList.add('is-open');
      box.classList.add('is-open');
      statusEl.textContent = 'startet …';
      try {
        const antwort = await fetch(pfad, {
          method: 'POST',
          credentials: 'include',
        });
        if (antwort.status === 409) {
          statusEl.textContent = 'läuft bereits (anderswo offen?)';
          return;
        }
        if (!antwort.ok) {
          statusEl.textContent = 'Start fehlgeschlagen';
          return;
        }
        const daten = await antwort.json();
        quellen = daten.quellen || [];
        sessionId = daten.session_id ?? null;
        aktuelleQuelle = 0;
        zeigeQuellenstand();
        // 200 ohne session_id heisst: zbot hat gar keinen Browser gestartet
        // (z. B. "Alle Quellen antworten - nichts freizuschalten"). Dann gibt
        // es auch keinen Strom, an den man sich haengen koennte — der
        // Upgrade wuerde mit 409 abgelehnt und im Kasten staende das
        // nichtssagende "Verbindung beendet". Lieber zbots eigenen Grund
        // zeigen und es dabei belassen.
        if (sessionId === null) {
          statusEl.textContent = daten.hinweis || 'Nichts freizuschalten';
          return;
        }
      } catch {
        statusEl.textContent = 'Start fehlgeschlagen';
        return;
      }

      const protokoll = location.protocol === 'https:' ? 'wss:' : 'ws:';
      ws = new WebSocket(`${protokoll}//${location.host}/api/admin/zbot-unlock/stream`);
      ws.binaryType = 'blob';
      ws.addEventListener('message', async (ev) => {
        if (typeof ev.data === 'string') return; // fuer spaetere Textnachrichten reserviert
        const bild = await createImageBitmap(ev.data);
        // Canvas auf die ECHTE Bildgroesse ziehen, statt das Bild auf feste 1280x800
        // zu strecken: das Screencast-Frame hat die Viewport-Groesse des Tabs, und
        // jede Streckung verschiebt die zurueckgerechneten Klickkoordinaten
        // (s. berechneSeitenKoordinate). Nur bei Aenderung setzen - eine Zuweisung an
        // canvas.width loescht den Inhalt und setzt den 2D-Kontext zurueck.
        if (canvas.width !== bild.width || canvas.height !== bild.height) {
          canvas.width = bild.width;
          canvas.height = bild.height;
        }
        ctx.drawImage(bild, 0, 0);
      });
      ws.addEventListener('close', () => {
        statusEl.textContent = 'Verbindung beendet';
      });
      canvas.focus();
    },
  };
}
